import { Box, Chip, Stack, Typography } from '@mui/material';

import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import {
  MarketingCtaBand,
  MarketingFeatureGrid,
  MarketingHero,
  MarketingStatRow,
} from '@/components/Marketing/blocks';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { ServicesPlatformDiagram } from '@/components/Marketing/ServicesPlatformDiagram';
import { usePageMeta } from '@/hooks/usePageMeta';

const VETERAN_HIGHLIGHTS = [
  'FHA from 3.5% down',
  'Cash-out in as little as 3 days',
  'Conventional, income-driven',
  'Investor programs to 90% financing',
  'DSCR for long-term rentals',
];

const NICHE_PROGRAMS = [
  'Asset Qualifier Mortgages',
  'Bank Statement Mortgages',
  'Non-Warrantable Condos',
  'Buy-before-you-sell',
  'Reverse Mortgages',
  'Bridge Loans',
  'Commercial Loans',
  'Mixed-use Loans',
  'New Construction',
  'Construction Loans',
];

export default function ServicesPage() {
  usePageMeta(
    'Mortgage Services',
    'Wholesale rates, loan estimates, pre-approval, and investor programs from Unbeatable Loans.'
  );
  return (
    <MarketingPageShell
      title="We're Unbeatable."
      subtitle="Our tech replaces buyers agents. Wholesale rates, instant lender matching, and offer tools built for homebuyers and investors."
      heroAppend={<ServicesPlatformDiagram />}
    >
      <Box className="taste-rise">
        <MarketingHero
          title="We started Unbeatable to help buyers save thousands."
          description="Our platform is designed for first-time homebuyers, homeowners, and investors to cut out middlemen. Create loan estimates, view properties, submit offers, and negotiate—with instant submission to 330+ lenders."
        />
      </Box>

      <Box className="taste-rise-delay-1">
        <MarketingFeatureGrid
          columns={{ xs: 12, md: 4 }}
          items={[
            {
              title: 'Mortgage Pre-Approval',
              body: 'Get pre-approved or make a plan after a quick consultation.',
            },
            {
              title: 'House Shopping',
              body: 'Start house shopping with numbers, not just listing links.',
            },
            {
              title: 'Loan Application',
              body: 'Quick and easy loan application to complete online.',
            },
          ]}
        />
      </Box>

      <Box className="taste-rise-delay-2">
        <MarketingStatRow
          stats={[
            { value: '7+', label: 'Years of experience' },
            { value: '$81M+', label: 'Funded' },
            { value: '331+', label: 'Mortgage lenders' },
            { value: '50', label: 'States' },
          ]}
        />
      </Box>

      <Stack spacing={3} sx={{ mt: { xs: 3, md: 4 } }}>
        <TasteSectionCard
          sx={{ p: { xs: 2.5, md: 3.5 } }}
          className="taste-rise-delay-2"
        >
          <Typography
            variant="h5"
            component="h2"
            className="font-display"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'var(--primary-navy)',
              mb: 1.5,
            }}
          >
            Proudly offering 0% down and strategies for veterans
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2.5,
              lineHeight: 1.7,
              color: 'var(--on-surface-variant)',
            }}
          >
            FHA from 3.5% down for first purchases, house hacks, and more.
            Cash-out and debt consolidation with funds in as little as 3
            business days while keeping your low primary rate. Conventional
            loans driven by your income, credit, and assets. Investor programs
            with up to 90% financing. DSCR and long-term rental strategies
            focused on cash flow.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {VETERAN_HIGHLIGHTS.map((highlight) => (
              <Chip
                key={highlight}
                label={highlight}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: 'var(--surface-container-lowest)',
                  border:
                    '1px solid color-mix(in srgb, var(--outline-variant) 60%, transparent)',
                }}
              />
            ))}
          </Stack>
        </TasteSectionCard>

        <TasteSectionCard
          sx={{ p: { xs: 2.5, md: 3.5 } }}
          className="taste-rise-delay-3"
        >
          <Typography
            variant="h5"
            component="h2"
            className="font-display"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'var(--primary-navy)',
              mb: 1.5,
            }}
          >
            Our mission is your Unbeatable mortgage
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2.5,
              lineHeight: 1.7,
              color: 'var(--on-surface-variant)',
            }}
          >
            Beyond popular products we offer niche options built for the deals
            that don&apos;t fit a standard box:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {NICHE_PROGRAMS.map((program) => (
              <Chip
                key={program}
                label={program}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderColor:
                    'color-mix(in srgb, var(--outline-variant) 70%, transparent)',
                  color: 'var(--on-surface-variant)',
                }}
              />
            ))}
          </Stack>
        </TasteSectionCard>
      </Stack>

      <Box className="taste-rise-delay-3">
        <MarketingCtaBand
          title="Explore loan estimates and offers"
          subtitle="Use the platform to compare programs and move forward with confidence."
          to="/main"
          buttonLabel="Go to app"
        />
      </Box>
    </MarketingPageShell>
  );
}
